import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useTheme, useBrand } from '../context/ColorContext';
import { Award, Globe, Factory, Zap, Users, Shield } from 'lucide-react';

const DEFAULT_HAKKIMIZDA = {
    heroTitle: "Hikayemiz",
    heroSubtitle: "Kayalar Kimya; insana, bilime ve üretmeye odaklı yaklaşımıyla dünyanın önde gelen mobilya boya ve vernik üreticilerinden biridir.",
    stats: [
        { value: "1976", label: "Kuruluş Yılı" },
        { value: "142.000", label: "Ton Kapasite" },
        { value: "73.", label: "Dünya Sıralaması" },
        { value: "3000+", label: "Ürün Çeşidi" }
    ],
    section1: {
        title: "Küresel Güç ve Liderlik",
        p1: "Yatırımlarına her geçen gün yenisini ekleyen Kayalar Kimya, 142 bin tonluk üretim kapasitesi ve AR-GE merkezinden aldığı güçle kullanıcılarına dünya standartlarında boya ve vernik ürünleri sunmaktadır.",
        p2: "Sektörünün dünya çapında en prestijli şirketlerini belirleyen Coatings World listesine 2024 yılında 73. sıradan giren Kayalar Kimya, modern otomasyon sistemine sahip tesislerinde 3000 farklı ürün çeşidi ile üretimine devam etmektedir.",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800"
    },
    section2: {
        title: "İnovasyonun Öncüsü",
        p1: "Kayalar Kimya, Türkiye'de mobilya boyaları sektöründe 2011 yılında ilk AR-GE merkezi unvanını almaya hak kazanmış olmasının yanı sıra, sektördeki ilk “Teknik Destek, Uygulama ve Simülasyon Merkezi’ni” kurarak inovasyon liderliğini pekiştirmiştir.",
        p2: "Bugün şirket; mobilya boyalarında GENÇ (1979), dekoratif boyalarda DÜFA (2005), sanayi boyalarında GENÇ NOROO (2012) ve dış mekân ahşap koruma sistemlerinde WOODSOL (2017) markalarıyla küresel pazarda faaliyet göstermektedir."
    },
    section3: {
        tag: "Boyanın Ustası",
        title: "Renklerin Sınırı Yok",
        desc: "“Boyanın Ustası” sloganıyla pazara renk getiren markamız, Mix makine renklendirme teknolojisi ile müşterilerine sonsuz renk seçeneği sunmaktadır.",
        features: ["Hızlı Çözüm", "Usta İşbirliği", "Güvenli Yapılar"]
    }
};

const fetchHakkimizda = async () => {
    const res = await axios.get('/api/pages/hakkimizda');
    return res.data;
};

const Hakkimizda = () => {
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';

    const { data: pageData, isLoading } = useQuery({
        queryKey: ['page_hakkimizda'],
        queryFn: fetchHakkimizda,
        retry: false
    });

    const content = pageData || DEFAULT_HAKKIMIZDA;

    const stats = [
        { icon: <Factory size={24} />, value: content.stats?.[0]?.value || '1976', label: content.stats?.[0]?.label || 'Kuruluş Yılı' },
        { icon: <Globe size={24} />, value: content.stats?.[1]?.value || '142.000', label: content.stats?.[1]?.label || 'Ton Kapasite' },
        { icon: <Award size={24} />, value: content.stats?.[2]?.value || '73.', label: content.stats?.[2]?.label || 'Dünya Sıralaması' },
        { icon: <Users size={24} />, value: content.stats?.[3]?.value || '3000+', label: content.stats?.[3]?.label || 'Ürün Çeşidi' },
    ];

    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png',
        'woodsol': '/img/woodsol-maskot.png'
    };

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center">Yükleniyor...</div>;
    }

    return (
        <div className={`min-h-screen pt-32 pb-20 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <div className="container mx-auto px-4 md:px-0 text-center mb-24">
                <span className="text-sm font-bold tracking-[0.4em] opacity-60 uppercase mb-4 block">Kayalar Kimya & {activeBrand.name}</span>
                <h1 className="text-5xl md:text-8xl font-black uppercase mb-8 tracking-tighter leading-none">{content.heroTitle}</h1>
                <p className="max-w-3xl mx-auto text-xl md:text-2xl font-light leading-relaxed opacity-80">
                    {content.heroSubtitle}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="container mx-auto px-4 md:px-0 mb-32">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
                    {stats.map((stat, idx) => (
                        <div key={idx} className={`p-8 rounded-[2.5rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-xl flex flex-col items-center text-center transition-transform hover:scale-105`}>
                            <div className="mb-4" style={{ color: activeBrand.primaryColor }}>{stat.icon}</div>
                            <div className="text-3xl md:text-5xl font-black mb-2">{stat.value}</div>
                            <div className="text-xs font-bold uppercase tracking-widest opacity-60">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Core Content - Alternating Sections */}
            <div className="container mx-auto px-4 md:px-0 space-y-32">

                {/* Section 1: History */}
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <div className="lg:w-1/2">
                        <h2 className="text-4xl md:text-6xl font-black uppercase mb-8 tracking-tighter">{content.section1?.title}</h2>
                        <div className="space-y-6 text-lg font-light opacity-75 leading-relaxed">
                            <p>{content.section1?.p1}</p>
                            <p>{content.section1?.p2}</p>
                        </div>
                    </div>
                    <div className="lg:w-1/2 relative">
                        <div className={`aspect-square rounded-[3rem] overflow-hidden border ${isDark ? 'border-white/10' : 'border-black/5'} relative group`}>
                            <img src={content.section1?.image || "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=800"} alt="Üretim" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                            <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                        </div>
                        {/* Floating Decoration */}
                        <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full blur-[100px] opacity-20" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                    </div>
                </div>

                {/* Section 2: Partnership & Evolution */}
                <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
                    <div className="lg:w-1/2">
                        <h2 className="text-4xl md:text-6xl font-black uppercase mb-8 tracking-tighter">{content.section2?.title}</h2>
                        <div className="space-y-6 text-lg font-light opacity-75 leading-relaxed">
                            <p>{content.section2?.p1}</p>
                            <p>{content.section2?.p2}</p>
                        </div>
                    </div>
                    <div className="lg:w-1/2">
                        <div className={`p-12 rounded-[3rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-3xl relative overflow-hidden group`}>
                            <div className="flex flex-col gap-8 relative z-10">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center p-2 shadow-xl">
                                        <img src={activeBrand.logo} alt={activeBrand.name} className="w-full h-auto" />
                                    </div>
                                    <div className="h-0.5 w-12" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                                    <div className="text-2xl font-black uppercase tracking-tighter">KAYALAR KİMYA</div>
                                </div>
                                <h3 className="text-3xl font-black uppercase">Sürdürülebilirlik</h3>
                                <p className="opacity-70">İnsan sağlığına ve çevreye duyarlı yaklaşımımızla, karbon ayak izimizi azaltıyor ve enerji verimliliğini projelerimizin merkezine koyuyoruz.</p>
                            </div>
                            <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[120px] opacity-10 group-hover:opacity-20 transition-opacity" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Mix Technology & Vision */}
                <div className="rounded-[4rem] p-12 md:p-24 relative overflow-hidden text-white group" style={{ backgroundColor: activeBrand.primaryColor }}>
                    <div className="relative z-10 max-w-4xl">
                        <span className="text-xs font-bold tracking-[0.4em] uppercase opacity-70 mb-4 block">{content.section3?.tag}</span>
                        <h2 className="text-4xl md:text-7xl font-black uppercase mb-8 leading-none tracking-tighter">{content.section3?.title}</h2>
                        <p className="text-xl md:text-2xl font-light leading-relaxed mb-12 opacity-90">
                            {content.section3?.desc}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <Zap size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[0]}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <Users size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[1]}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <Shield size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[2]}</span>
                            </div>
                        </div>
                    </div>

                    {/* Mascot peeking from the side */}
                    <div className="absolute bottom-6 -right-10 w-64 h-64 md:w-96 md:h-96 opacity-30 pointer-events-none animate-bounce-subtle group-hover:opacity-50 transition-opacity z-[60]">
                        <img
                            src={mascotImages[activeBrand.id] || mascotImages['dufa']}
                            alt={activeBrand.name}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.src = mascotImages['dufa']; }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hakkimizda;
